// apps/web/src/components/deliverables/DeliverableManager.tsx
// Deliverables management UI component

import type React from 'react';
import { useState, useEffect } from 'react';
import type { Deliverable } from '../../types/deliverable';
import { apiClient } from '../../lib/apiClient';

interface DeliverableManagerProps {
  campaignId?: string;
}

export function DeliverableManager({ campaignId }: DeliverableManagerProps) {
  const [deliverables, setDeliverables] = useState<Deliverable[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    const fetchDeliverables = async () => {
      try {
        setLoading(true);
        const response = await apiClient.get('/api/v1/deliverables');
        setDeliverables(response.data.deliverables);
      } catch (err) {
        setError('Failed to load deliverables');
        console.error('Error loading deliverables:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchDeliverables();
  }, []);

  const handleCreateDeliverable = async (deliverableData: any) => {
    try {
      const response = await apiClient.post('/api/v1/deliverables', deliverableData);
      setDeliverables([...deliverables, response.data]);
      setShowForm(false);
    } catch (err) {
      setError('Failed to create deliverable');
      console.error('Error creating deliverable:', err);
    }
  };

  if (loading) {
    return <div className="loading">Loading deliverables...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  return (
    <div className="deliverable-manager">
      <div className="header">
        <h2>Deliverables Management</h2>
        <button 
          onClick={() => setShowForm(!showForm)}
          className="btn btn-primary"
        >
          {showForm ? 'Cancel' : 'Add Deliverable'}
        </button>
      </div>

      {showForm && (
        <DeliverableForm 
          onSubmit={handleCreateDeliverable} 
          onCancel={() => setShowForm(false)} 
          campaignId={campaignId}
        />
      )}

      <div className="deliverables-list">
        {deliverables.map(deliverable => (
          <DeliverableCard key={deliverable.id} deliverable={deliverable} />
        ))}
      </div>
    </div>
  );
}

// Helper component
function DeliverableCard({ deliverable }: { deliverable: Deliverable }) {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PENDING': return 'status-pending';
      case 'IN_PROGRESS': return 'status-in-progress';
      case 'SUBMITTED': return 'status-submitted';
      case 'UNDER_REVIEW': return 'status-under-review';
      case 'APPROVED': return 'status-approved';
      case 'REJECTED': return 'status-rejected';
      case 'COMPLETED': return 'status-completed';
      default: return 'status-default';
    }
  };

  return (
    <div className="deliverable-card">
      <div className="deliverable-header">
        <h3>{deliverable.title}</h3>
        <span className={`status-badge ${getStatusColor(deliverable.status)}`}>
          {deliverable.status}
        </span>
      </div>
      <p>{deliverable.description}</p>
      <div className="deliverable-details">
        <p><strong>Type:</strong> {deliverable.type}</p>
        <p><strong>Due:</strong> {deliverable.dueDate}</p>
        <p><strong>Creator:</strong> {deliverable.creatorId.substring(0, 8)}...</p>
      </div>
      <div className="actions">
        <button className="btn btn-secondary">View</button>
        <button className="btn btn-warning">Edit</button>
        <button className="btn btn-success">Submit</button>
      </div>
    </div>
  );
}

// Helper component
function DeliverableForm({ onSubmit, onCancel, campaignId }: { 
  onSubmit: any; 
  onCancel: any;
  campaignId?: string;
}) {
  const [formData, setFormData] = useState({
    campaignId: campaignId || '',
    creatorId: '',
    title: '',
    description: '',
    dueDate: '',
    status: 'PENDING',
    type: 'CONTENT'
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="deliverable-form">
      <h3>Create New Deliverable</h3>
      <input
        type="text"
        placeholder="Title"
        value={formData.title}
        onChange={(e) => setFormData({...formData, title: e.target.value})}
        required
      />
      <textarea
        placeholder="Description"
        value={formData.description}
        onChange={(e) => setFormData({...formData, description: e.target.value})}
      />
      <input
        type="date"
        value={formData.dueDate}
        onChange={(e) => setFormData({...formData, dueDate: e.target.value})}
      />
      <select
        value={formData.type}
        onChange={(e) => setFormData({...formData, type: e.target.value})}
      >
        <option value="CONTENT">Content</option>
        <option value="DESIGN">Design</option>
        <option value="VIDEO">Video</option>
        <option value="COPY">Copy</option>
        <option value="OTHER">Other</option>
      </select>
      <input
        type="text"
        placeholder="Creator ID"
        value={formData.creatorId}
        onChange={(e) => setFormData({...formData, creatorId: e.target.value})}
        required
      />
      <div className="form-actions">
        <button type="submit" className="btn btn-primary">Create</button>
        <button type="button" onClick={onCancel} className="btn btn-secondary">Cancel</button>
      </div>
    </form>
  );
}